import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { SEO } from "@/components/SEO"
import { ServiceSchema } from "@/components/schema/ServiceSchema"
import { BreadcrumbSchema } from "@/components/schema/BreadcrumbSchema"
import {
    ArrowRight,
    Search,
    PenLine,
    Code,
    Plug,
    Activity,
    TrendingUp,
    MessageSquare,
    Eye,
    Bot,
    Workflow,
    Shield,
    Cloud,
    Database,
    Cpu,
    Brain,
} from "lucide-react"
import { Link } from "react-router-dom"

// ─── Data Arrays ────────────────────────────────────────────────

const buildPhases = [
    {
        num: "01",
        title: "Discovery & Assessment",
        description:
            "We audit your data landscape, existing infrastructure, and business objectives. We identify where AI creates measurable value — not where it sounds impressive.",
        deliverable: "AI Opportunity Assessment with ROI projections.",
        icon: Search,
    },
    {
        num: "02",
        title: "AI Architecture Design",
        description:
            "Our architects design the system blueprint — model selection, data pipelines, integration points, security boundaries, and scaling strategy. Every decision is documented and reviewed with your engineering team.",
        deliverable: "Technical Architecture Document & project roadmap.",
        icon: PenLine,
    },
    {
        num: "03",
        title: "Development & Training",
        description:
            "We build and train models in iterative development sprints with weekly demos so you see progress, not just status updates.",
        deliverable: "Trained models with benchmark performance metrics.",
        icon: Code,
    },
    {
        num: "04",
        title: "Integration & Testing",
        description:
            "The AI system integrates with your existing stack — CRM, ERP, data warehouse, APIs. Rigorous testing: unit, integration, load, adversarial, and bias audits.",
        deliverable: "Fully integrated system in staging environment.",
        icon: Plug,
    },
    {
        num: "05",
        title: "Deployment & Launch",
        description:
            "Production deployment with zero-downtime rollout, monitoring dashboards, alerting, and rollback procedures. We don't throw it over the wall — we deploy alongside your ops team.",
        deliverable: "Live production system with observability.",
        icon: Activity,
    },
    {
        num: "06",
        title: "Optimization & Support",
        description:
            "Post-launch model monitoring, drift detection, retraining pipelines, and performance optimization. Optional managed AI services for long-term operational support.",
        deliverable: "Monthly performance reports & continuous improvement.",
        icon: TrendingUp,
    },
]

const solutions = [
    {
        num: "01",
        title: "Predictive Analytics Platforms",
        description:
            "Forecast demand, churn, equipment failure, and market shifts using your historical data. We build models that don't just predict — they prescribe the optimal action.",
        tags: ["Time Series Forecasting", "Anomaly Detection", "Demand Planning", "Risk Scoring"],
        icon: TrendingUp,
    },
    {
        num: "02",
        title: "Natural Language Processing",
        description:
            "Extract meaning from documents, emails, support tickets, and unstructured text at scale. From entity extraction to semantic search to document classification.",
        tags: ["Named Entity Recognition", "Sentiment Analysis", "Document Intelligence", "Semantic Search"],
        icon: MessageSquare,
    },
    {
        num: "03",
        title: "Computer Vision Solutions",
        description:
            "Automated visual inspection, object detection, medical imaging analysis, and document OCR. Production-grade pipelines that process thousands of images per minute.",
        tags: ["Object Detection", "Image Classification", "Visual QA", "OCR Pipelines"],
        icon: Eye,
    },
    {
        num: "04",
        title: "Recommendation Engines",
        description:
            "Personalized product recommendations, content curation, and next-best-action engines that drive engagement and revenue. Built on your behavioral data, not generic models.",
        tags: ["Collaborative Filtering", "Content-Based", "Hybrid Models", "Real-Time Personalization"],
        icon: Brain,
    },
    {
        num: "05",
        title: "Custom LLM Applications",
        description:
            "Enterprise chatbots, AI copilots, document summarizers, code generation tools, and workflow agents built on GPT-4, Claude, Llama, or fine-tuned open-source models. RAG pipelines, guardrails, and hallucination controls built in.",
        tags: ["RAG Pipelines", "AI Agents", "Fine-Tuning", "Prompt Engineering", "Guardrails"],
        icon: Bot,
    },
    {
        num: "06",
        title: "Business Process Automation",
        description:
            "End-to-end workflow automation combining AI decision-making with RPA execution. Invoice processing, claims adjudication, lead scoring, and compliance monitoring — fully autonomous.",
        tags: ["Intelligent Automation", "Document Processing", "Decision Engines", "Workflow Orchestration"],
        icon: Workflow,
    },
]

const techStack = [
    {
        category: "AI & ML Frameworks",
        icon: Cpu,
        items: [
            "PyTorch",
            "TensorFlow",
            "Hugging Face Transformers",
            "LangChain",
            "LlamaIndex",
            "scikit-learn",
            "XGBoost",
            "spaCy",
            "OpenCV",
        ],
    },
    {
        category: "Cloud & Infrastructure",
        icon: Cloud,
        items: [
            "AWS SageMaker",
            "AWS Bedrock",
            "Azure AI",
            "Azure OpenAI Service",
            "GCP Vertex AI",
            "Kubernetes",
            "Terraform",
            "Docker",
        ],
    },
    {
        category: "Data & Orchestration",
        icon: Database,
        items: [
            "Snowflake",
            "Databricks",
            "Apache Airflow",
            "Kafka",
            "PostgreSQL",
            "Redis",
            "Pinecone",
            "Weaviate",
            "MLflow",
        ],
    },
    {
        category: "LLMs & Foundation Models",
        icon: Brain,
        items: [
            "GPT-4 / GPT-4o",
            "Claude (Anthropic)",
            "Llama 3",
            "Mistral",
            "Gemini",
            "Fine-tuned OSS Models",
        ],
    },
]

const caseStudies = [
    {
        industry: "Healthcare",
        title: "Predictive Patient Readmission System",
        challenge:
            "A regional health system was experiencing 18% 30-day readmission rates, costing millions in CMS penalties and poor patient outcomes.",
        solution:
            "A multi-variable predictive model analyzing 47 clinical and social determinants of health. Integrated directly into their Epic EHR workflow so care teams receive risk scores at discharge.",
        tech: "Python, XGBoost, Epic FHIR API, AWS SageMaker",
        metrics: [
            { value: "11.2%", label: "Readmission Rate" },
            { value: "$2.4M", label: "Annual Savings" },
            { value: "89%", label: "Model AUC" },
        ],
    },
    {
        industry: "FinTech",
        title: "Real-Time Fraud Detection Engine",
        challenge:
            "High false positive rates in their fraud detection system were blocking legitimate transactions and losing merchants.",
        solution:
            "A custom ensemble model combining gradient-boosted trees with a graph neural network for transaction pattern analysis. Real-time inference under 50ms per transaction with adaptive thresholds per merchant profile.",
        tech: "PyTorch, Neo4j, Apache Kafka, Kubernetes, GCP Vertex AI",
        metrics: [
            { value: "62%", label: "Fewer False Positives" },
            { value: "91%", label: "Fraud Catch Rate" },
            { value: "23ms", label: "Avg Latency" },
            { value: "$8.1M", label: "Recovered Revenue" },
        ],
    },
    {
        industry: "Manufacturing",
        title: "Visual Quality Inspection Automation",
        challenge:
            "A precision parts manufacturer relied on manual visual inspection for defect detection, achieving only 82% accuracy with 3 full-time inspectors per shift.",
        solution:
            "A computer vision pipeline using custom-trained YOLO models for real-time defect detection across 14 defect categories. Camera integration on 3 production lines with an operator dashboard for exception handling.",
        tech: "PyTorch, YOLOv8, OpenCV, NVIDIA Triton, Edge on NVIDIA Jetson",
        metrics: [
            { value: "97.3%", label: "Detection Accuracy" },
            { value: "4x", label: "Throughput Increase" },
            { value: "4.5mo", label: "ROI Breakeven" },
        ],
    },
]

const engagementModels = [
    {
        title: "Proof of Concept",
        duration: "4–6 weeks",
        description:
            "Validate the AI opportunity with a working prototype using your real data. Includes feasibility analysis, model benchmarking, and a go/no-go recommendation.",
        bestFor: "Teams exploring a new use case before committing.",
        pricing: "Fixed scope. Fixed price.",
        features: [
            "Working prototype with your data",
            "Feasibility analysis & benchmarks",
            "Go / no-go recommendation",
            "Technical architecture outline",
        ],
        highlighted: false,
    },
    {
        title: "Full Build",
        duration: "3–6 months",
        description:
            "End-to-end design, development, and deployment of a production AI system. Phased delivery with milestone-based payments and weekly sprint demos.",
        bestFor: "Organizations ready to move from pilot to production.",
        pricing: "Milestone-based. Dedicated team.",
        features: [
            "Full 6-phase build methodology",
            "Dedicated engineering team",
            "Weekly sprint demos",
            "Production deployment & handoff",
        ],
        highlighted: true,
    },
    {
        title: "Build + Operate",
        duration: "6–12+ months",
        description:
            "We build the system AND run it. Includes ongoing model monitoring, retraining, optimization, and 24/7 operational support via our Managed AI Services.",
        bestFor: "Enterprises that want outcomes without building an internal MLOps team.",
        pricing: "Monthly retainer. Full accountability.",
        features: [
            "Everything in Full Build",
            "24/7 operational support",
            "Model monitoring & retraining",
            "Monthly performance reports",
        ],
        highlighted: false,
    },
]

// ─── Component ──────────────────────────────────────────────────

const CustomAISolutions = () => {
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="Custom AI Solutions | Enterprise AI Engineering | RSA Tech Group"
                description="We design, build, and deploy custom AI systems — from predictive analytics and NLP to computer vision and LLM applications. SOC 2 certified. Enterprise-grade."
            />
            <ServiceSchema
                name="Custom AI Solutions"
                description="We design, build, and deploy custom AI systems — from predictive analytics and NLP to computer vision and LLM applications. SOC 2 certified. Enterprise-grade."
                url="https://rsatech.com/what-we-build/custom-ai-solutions"
                serviceType="AI Engineering Services"
            />
            <BreadcrumbSchema
                items={[
                    { name: "Home", url: "https://rsatech.com" },
                    { name: "What We Build", url: "https://rsatech.com/what-we-do" },
                    { name: "Custom AI Solutions", url: "https://rsatech.com/what-we-build/custom-ai-solutions" },
                ]}
            />
            <Navigation />

            {/* ─── Section 1: Hero ─── */}
            <div className="relative pt-48 pb-24 overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_30%,rgba(28,43,110,0.3),transparent_60%)]" />
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#1C2B6E]/10 rounded-full blur-[150px]" />
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#FDA400]/[0.04] rounded-full blur-[120px]" />

                <div className="section-container relative z-10 text-center">
                    <div className="max-w-4xl mx-auto">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-6">
                            Custom AI Engineering
                        </p>
                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-[1.1] tracking-tight">
                            AI Solutions Engineered for{" "}
                            <span className="text-[#FDA400]">Your Business</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-12 font-light leading-relaxed">
                            From concept to production — we design, build, and deploy custom AI systems that solve problems unique to your enterprise.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/contact"
                                className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-[#1C2B6E] bg-[#FDA400] rounded-full hover:bg-[#f5b800] transition-all duration-300 shadow-[0_8px_30px_rgba(253,164,0,0.3)] hover:shadow-[0_12px_40px_rgba(253,164,0,0.4)] hover:-translate-y-0.5"
                            >
                                Describe Your AI Challenge <ArrowRight className="ml-2 h-5 w-5" />
                            </Link>
                            <Link
                                to="/what-we-do/managed-services-aiops"
                                className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white border-2 border-white/20 rounded-full hover:border-[#FDA400]/50 hover:text-[#FDA400] transition-all duration-300"
                            >
                                Explore Managed AI Services
                            </Link>
                        </div>

                        {/* Hero stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-10 border-t border-white/10">
                            {[
                                { value: "500+", label: "Implementations" },
                                { value: "99.99%", label: "Uptime SLA" },
                                { value: "9", label: "Industries" },
                                { value: "24/7", label: "Support" },
                            ].map((stat, i) => (
                                <div key={i} className="text-center">
                                    <div className="text-3xl md:text-4xl font-black text-[#FDA400] mb-2">{stat.value}</div>
                                    <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── Section 2: How We Build (6-phase) ─── */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.15]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-20">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Our Process
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            How We{" "}
                            <span className="text-[#FDA400]">Build</span>
                        </h2>
                        <p className="text-slate-500 text-lg mt-6 max-w-3xl mx-auto font-light leading-relaxed">
                            A proven 6-phase methodology that takes your AI initiative from whiteboard to production.
                        </p>
                    </div>

                    {/* Desktop stepper */}
                    <div className="max-w-7xl mx-auto">
                        {/* Row 1: phases 1-3 */}
                        <div className="hidden md:block relative">
                            <div className="absolute top-12 left-[16%] right-[16%] h-[2px] bg-gradient-to-r from-[#FDA400]/30 via-[#1C2B6E]/20 to-[#FDA400]/30 z-0" />
                            <div className="grid md:grid-cols-3 gap-8 relative z-10">
                                {buildPhases.slice(0, 3).map((phase) => (
                                    <div key={phase.num} className="flex flex-col items-center text-center">
                                        <div className="w-24 h-24 bg-white border-2 border-[#FDA400]/30 rounded-full flex flex-col items-center justify-center mb-6 shadow-[0_4px_20px_rgba(28,43,110,0.08)] relative">
                                            <span className="text-xs font-bold text-[#FDA400] uppercase tracking-wider">Phase</span>
                                            <span className="text-2xl font-black text-[#1C2B6E]">{phase.num}</span>
                                        </div>
                                        <phase.icon className="w-5 h-5 text-[#FDA400] mb-3" />
                                        <h3 className="text-lg font-black text-[#1C2B6E] tracking-tight mb-2">{phase.title}</h3>
                                        <p className="text-slate-500 text-sm font-light leading-relaxed mb-3 px-2">{phase.description}</p>
                                        <p className="text-xs text-[#FDA400] font-semibold">{phase.deliverable}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Vertical connector between rows on desktop */}
                        <div className="hidden md:flex justify-center my-6">
                            <div className="w-[2px] h-12 bg-gradient-to-b from-[#FDA400]/30 to-[#1C2B6E]/20" />
                        </div>

                        {/* Row 2: phases 4-6 */}
                        <div className="hidden md:block relative">
                            <div className="absolute top-12 left-[16%] right-[16%] h-[2px] bg-gradient-to-r from-[#FDA400]/30 via-[#1C2B6E]/20 to-[#FDA400]/30 z-0" />
                            <div className="grid md:grid-cols-3 gap-8 relative z-10">
                                {buildPhases.slice(3, 6).map((phase) => (
                                    <div key={phase.num} className="flex flex-col items-center text-center">
                                        <div className="w-24 h-24 bg-white border-2 border-[#FDA400]/30 rounded-full flex flex-col items-center justify-center mb-6 shadow-[0_4px_20px_rgba(28,43,110,0.08)] relative">
                                            <span className="text-xs font-bold text-[#FDA400] uppercase tracking-wider">Phase</span>
                                            <span className="text-2xl font-black text-[#1C2B6E]">{phase.num}</span>
                                        </div>
                                        <phase.icon className="w-5 h-5 text-[#FDA400] mb-3" />
                                        <h3 className="text-lg font-black text-[#1C2B6E] tracking-tight mb-2">{phase.title}</h3>
                                        <p className="text-slate-500 text-sm font-light leading-relaxed mb-3 px-2">{phase.description}</p>
                                        <p className="text-xs text-[#FDA400] font-semibold">{phase.deliverable}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Mobile vertical timeline */}
                        <div className="md:hidden space-y-0">
                            {buildPhases.map((phase, idx) => (
                                <div key={phase.num} className="flex gap-5">
                                    {/* Timeline column */}
                                    <div className="flex flex-col items-center">
                                        <div className="w-16 h-16 bg-white border-2 border-[#FDA400]/30 rounded-full flex flex-col items-center justify-center shadow-[0_4px_20px_rgba(28,43,110,0.08)] flex-shrink-0">
                                            <span className="text-[9px] font-bold text-[#FDA400] uppercase tracking-wider">Phase</span>
                                            <span className="text-lg font-black text-[#1C2B6E]">{phase.num}</span>
                                        </div>
                                        {idx < buildPhases.length - 1 && (
                                            <div className="w-[2px] flex-1 min-h-[24px] bg-gradient-to-b from-[#FDA400]/30 to-[#1C2B6E]/20" />
                                        )}
                                    </div>
                                    {/* Content */}
                                    <div className="pb-8 pt-2">
                                        <phase.icon className="w-4 h-4 text-[#FDA400] mb-2" />
                                        <h3 className="text-base font-black text-[#1C2B6E] tracking-tight mb-1">{phase.title}</h3>
                                        <p className="text-slate-500 text-sm font-light leading-relaxed mb-2">{phase.description}</p>
                                        <p className="text-xs text-[#FDA400] font-semibold">{phase.deliverable}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── Section 3: Solutions We Engineer ─── */}
            <section className="py-24 relative overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.15),transparent_70%)]" />
                <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#FDA400]/[0.03] rounded-full blur-[120px]" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#1C2B6E]/10 rounded-full blur-[150px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-20">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Capabilities
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1]">
                            Solutions We{" "}
                            <span className="text-[#FDA400]">Engineer</span>
                        </h2>
                        <p className="text-slate-400 text-lg mt-6 max-w-3xl mx-auto font-light leading-relaxed">
                            Every engagement is custom. These are the categories of AI systems we build most often.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
                        {solutions.map((item) => (
                            <div
                                key={item.num}
                                className="relative bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-3xl p-8 md:p-10 hover:bg-white/[0.07] hover:border-[#FDA400]/20 transition-all duration-500 group"
                            >
                                {/* Ghost number */}
                                <span className="absolute top-4 right-6 text-7xl font-black text-white/[0.03] leading-none select-none group-hover:text-white/[0.06] transition-colors duration-500">
                                    {item.num}
                                </span>

                                <div className="relative z-10">
                                    <div className="w-10 h-10 rounded-xl bg-[#FDA400]/10 flex items-center justify-center mb-5">
                                        <item.icon className="w-5 h-5 text-[#FDA400]" />
                                    </div>
                                    <h3 className="text-xl font-black text-white tracking-tight mb-3">
                                        {item.title}
                                    </h3>
                                    <p className="text-slate-400 text-sm font-light leading-relaxed mb-6">
                                        {item.description}
                                    </p>
                                    <div className="flex flex-wrap gap-2 pt-5 border-t border-white/[0.06]">
                                        {item.tags.map((tag, j) => (
                                            <span
                                                key={j}
                                                className="px-3 py-1.5 bg-white/[0.06] text-slate-300 text-xs font-medium rounded-full border border-white/[0.08] hover:bg-[#FDA400]/10 hover:border-[#FDA400]/30 hover:text-[#FDA400] transition-all duration-300 cursor-default"
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── Section 4: Tech Stack ─── */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.15]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-20">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Technology
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            Built on What{" "}
                            <span className="text-[#FDA400]">Works</span>
                        </h2>
                        <p className="text-slate-500 text-lg mt-6 max-w-3xl mx-auto font-light leading-relaxed">
                            We're framework-agnostic and cloud-agnostic. We pick the right tool for the problem.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
                        {techStack.map((col) => (
                            <div
                                key={col.category}
                                className="bg-white rounded-3xl border border-[#1C2B6E]/[0.06] p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_15px_50px_rgba(28,43,110,0.08)] transition-all duration-500"
                            >
                                <div className="w-10 h-10 rounded-xl bg-[#1C2B6E]/[0.06] flex items-center justify-center mb-5">
                                    <col.icon className="w-5 h-5 text-[#1C2B6E]/60" />
                                </div>
                                <h3 className="text-base font-black text-[#1C2B6E] tracking-tight mb-5">
                                    {col.category}
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {col.items.map((item, j) => (
                                        <span
                                            key={j}
                                            className="px-3 py-1.5 bg-[#1C2B6E]/[0.04] text-[#1C2B6E]/70 text-xs font-medium rounded-full border border-[#1C2B6E]/[0.06] hover:bg-[#FDA400]/10 hover:border-[#FDA400]/30 hover:text-[#FDA400] transition-all duration-300 cursor-default"
                                        >
                                            {item}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── Section 5: Case Studies ─── */}
            <section className="py-24 relative overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.15),transparent_70%)]" />
                <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-[#FDA400]/[0.03] rounded-full blur-[120px]" />
                <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#1C2B6E]/10 rounded-full blur-[150px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-20">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Case Studies
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1]">
                            Custom AI in{" "}
                            <span className="text-[#FDA400]">Production</span>
                        </h2>
                        <p className="text-slate-400 text-lg mt-6 max-w-3xl mx-auto font-light leading-relaxed">
                            Real solutions. Measurable outcomes.
                        </p>
                    </div>

                    <div className="space-y-8 max-w-6xl mx-auto">
                        {caseStudies.map((study, i) => (
                            <div
                                key={i}
                                className="relative bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-3xl overflow-hidden hover:border-[#FDA400]/20 transition-all duration-500"
                            >
                                <div className="flex flex-col lg:flex-row">
                                    {/* Left: Content */}
                                    <div className="flex-1 p-8 md:p-10 lg:p-12">
                                        <span className="inline-block px-3 py-1 bg-[#FDA400]/10 text-[#FDA400] text-xs font-bold uppercase tracking-[0.15em] rounded-full mb-5">
                                            {study.industry}
                                        </span>
                                        <h3 className="text-2xl font-black text-white tracking-tight mb-6">
                                            {study.title}
                                        </h3>

                                        <div className="space-y-4 mb-6">
                                            <div>
                                                <span className="text-[10px] font-bold text-white/40 uppercase tracking-[0.15em]">Challenge</span>
                                                <p className="text-slate-400 text-sm font-light leading-relaxed mt-1">{study.challenge}</p>
                                            </div>
                                            <div>
                                                <span className="text-[10px] font-bold text-white/40 uppercase tracking-[0.15em]">What We Built</span>
                                                <p className="text-slate-400 text-sm font-light leading-relaxed mt-1">{study.solution}</p>
                                            </div>
                                        </div>

                                        <div className="pt-4 border-t border-white/[0.06]">
                                            <span className="text-[10px] font-bold text-white/30 uppercase tracking-[0.15em]">Tech Stack</span>
                                            <p className="text-slate-500 text-xs font-light mt-1">{study.tech}</p>
                                        </div>
                                    </div>

                                    {/* Right: Metrics */}
                                    <div className="lg:w-[340px] bg-gradient-to-br from-[#1C2B6E]/30 to-[#FDA400]/[0.05] p-8 md:p-10 lg:p-12 flex flex-col justify-center border-t lg:border-t-0 lg:border-l border-white/[0.06]">
                                        <span className="text-[10px] font-bold text-[#FDA400] uppercase tracking-[0.15em] mb-6">Results</span>
                                        <div className="space-y-6">
                                            {study.metrics.map((metric, j) => (
                                                <div key={j}>
                                                    <div className="text-4xl md:text-5xl font-black text-[#FDA400] leading-none">
                                                        {metric.value}
                                                    </div>
                                                    <div className="text-xs text-slate-400 uppercase tracking-wider mt-1">
                                                        {metric.label}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <p className="text-center text-slate-500 text-xs font-light mt-10 max-w-2xl mx-auto">
                        Note: Specific client details anonymized to protect confidentiality. Metrics represent verified project outcomes.
                    </p>
                </div>
            </section>

            {/* ─── Section 6: Engagement Models ─── */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.15]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 right-1/3 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-20">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Engagement
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            How We{" "}
                            <span className="text-[#FDA400]">Engage</span>
                        </h2>
                        <p className="text-slate-500 text-lg mt-6 max-w-3xl mx-auto font-light leading-relaxed">
                            Flexible models that match your pace and risk tolerance.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                        {engagementModels.map((model) => (
                            <div
                                key={model.title}
                                className={`partner-card group rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(28,43,110,0.12)] transition-all duration-500 hover:-translate-y-2 ${
                                    model.highlighted ? "ring-2 ring-[#FDA400]/20" : ""
                                }`}
                            >
                                {/* Top accent */}
                                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] z-10 ${model.highlighted ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"} transition-transform duration-500 origin-left`} />

                                {/* Glow */}
                                <div className="absolute -bottom-12 -right-12 w-44 h-44 bg-[#FDA400]/[0.04] rounded-full blur-[50px] group-hover:bg-[#FDA400]/10 group-hover:scale-110 transition-all duration-700" />

                                <div className="p-8 md:p-10 h-full flex flex-col relative z-10">
                                    {model.highlighted && (
                                        <span className="inline-block self-start px-3 py-1 bg-[#FDA400]/10 text-[#FDA400] text-[10px] font-bold uppercase tracking-[0.15em] rounded-full mb-4">
                                            Most Popular
                                        </span>
                                    )}

                                    <h3 className="text-2xl font-black text-[#1C2B6E] tracking-tight mb-1">
                                        {model.title}
                                    </h3>
                                    <p className="text-sm font-semibold text-[#FDA400] mb-4">{model.duration}</p>

                                    <p className="text-slate-500 text-sm font-light leading-relaxed mb-6">
                                        {model.description}
                                    </p>

                                    <ul className="space-y-2.5 mb-6 flex-grow">
                                        {model.features.map((f, j) => (
                                            <li key={j} className="flex items-start gap-2.5">
                                                <span className="w-1.5 h-1.5 rounded-full bg-[#FDA400] mt-1.5 flex-shrink-0" />
                                                <span className="text-slate-600 text-sm font-light">{f}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <div className="pt-5 border-t border-slate-100 mt-auto">
                                        <p className="text-xs text-slate-400 mb-1">
                                            <span className="font-semibold text-[#1C2B6E]/60">Best for:</span> {model.bestFor}
                                        </p>
                                        <p className="text-xs font-bold text-[#FDA400]">{model.pricing}</p>
                                    </div>

                                    <Link
                                        to="/contact"
                                        className={`mt-6 inline-flex items-center justify-center px-6 py-3 text-sm font-bold rounded-full transition-all duration-300 ${
                                            model.highlighted
                                                ? "bg-[#FDA400] text-[#1C2B6E] hover:bg-[#f5b800] shadow-[0_4px_20px_rgba(253,164,0,0.25)]"
                                                : "bg-[#1C2B6E]/[0.06] text-[#1C2B6E] hover:bg-[#FDA400] hover:text-[#1C2B6E]"
                                        }`}
                                    >
                                        Get Started <ArrowRight className="ml-2 h-4 w-4" />
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── Section 7: CTA ─── */}
            <section className="py-28 relative overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.2),transparent_70%)]" />
                <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-[#FDA400]/[0.04] rounded-full blur-[150px]" />

                <div className="section-container relative z-10 text-center">
                    <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-6">
                        Get Started
                    </p>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1] mb-6">
                        Describe Your{" "}
                        <span className="text-[#FDA400]">AI Challenge</span>
                    </h2>
                    <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto font-light leading-relaxed">
                        Tell us what you're trying to solve — we'll tell you how we'd build it.
                    </p>
                    <Link
                        to="/contact"
                        className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-[#1C2B6E] bg-[#FDA400] rounded-full hover:bg-[#f5b800] transition-all duration-300 shadow-[0_8px_30px_rgba(253,164,0,0.3)] hover:shadow-[0_12px_40px_rgba(253,164,0,0.4)] hover:-translate-y-0.5"
                    >
                        Start a Conversation <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                    <p className="text-slate-500 text-sm mt-6">
                        Or email us directly at{" "}
                        <a href="mailto:contact@rsatech.com" className="text-[#FDA400] hover:text-[#f5b800] transition-colors underline underline-offset-2">
                            contact@rsatech.com
                        </a>
                    </p>
                </div>
            </section>

            <Footer />
        </div>
    )
}

export default CustomAISolutions
